// Round 96b (Feb 2026) — Non-Dividend Expansion Section 6.
// "Growth" tab content for non-dividend stocks. Replaces the
// dividend-only Div Growth / Yield History / Triangle tabs that
// don't apply to pure growth stocks. Shows:
//
//   1. Revenue trend bar chart (annual, last ~8 years)
//   2. Diluted EPS trend bar chart (annual)
//   3. Quality Score breakdown — per-signal points using the
//      `non_dividend_quality_signals` rubric scored nightly by
//      services/non_dividend_quality_score.py.
//
// Massive-only: backend strips revenue/EPS when data_source !==
// "massive". Blank charts with an explanatory empty state are
// preferred over yfinance-fallback data per the spec.
import { useState, useEffect } from 'react';
import axios from 'axios';
import { TrendingUp, TrendingDown, Sparkles, AlertCircle } from 'lucide-react';
import HoverBar from './HoverBar';
import AbbrTip from '@/components/AbbrTip';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// --- helpers ---------------------------------------------------------

// Triangle compute mixes unit-suffixed (`value: 2.29, unit: "M"`) and
// raw-dollar (`value: 392070, unit: ""`) entries depending on whether
// Massive's snapshot scaled the figure server-side. Normalise to raw
// dollars so the chart Y-axis is on a single consistent scale.
const UNIT_MULT = { '': 1, M: 1e6, B: 1e9, T: 1e12, k: 1e3, K: 1e3 };
function toRawUsd(entry) {
  if (!entry || entry.value == null) return 0;
  const m = UNIT_MULT[entry.unit] != null ? UNIT_MULT[entry.unit] : 1;
  return entry.value * m;
}

function fmtUsdAuto(usd) {
  // Picks B / M / K / raw based on magnitude. Used for both bar
  // labels and Y-axis ticks so the units stay consistent.
  if (usd == null || Number.isNaN(usd)) return '—';
  const abs = Math.abs(usd);
  const sign = usd < 0 ? '-' : '';
  if (abs >= 1e9) return `${sign}$${(abs / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(1)}K`;
  return `${sign}$${abs.toFixed(0)}`;
}

function fmtEpsBar(value) {
  if (value == null) return '—';
  const sign = value < 0 ? '-' : '';
  return `${sign}$${Math.abs(value).toFixed(2)}`;
}

function fmtPct(v, digits = 1) {
  if (v == null || Number.isNaN(v)) return '—';
  const sign = v >= 0 ? '+' : '';
  return `${sign}${(v * 100).toFixed(digits)}%`;
}

// --- subcomponents ---------------------------------------------------

function MetricCard({ label, value, valueColor, sub }) {
  return (
    <div
      className="rounded-lg border bg-card p-4 text-center"
      data-testid={`non-div-growth-metric-${label.toLowerCase().replace(/\s/g, '-')}`}
    >
      <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground mb-1">{label}</p>
      <p className={`text-xl font-bold font-mono ${valueColor || ''}`}>{value}</p>
      {sub && <p className="text-[10px] text-muted-foreground mt-1">{sub}</p>}
    </div>
  );
}

function GrowthBarChart({ title, data, fmt, isDark, testId, normalize }) {
  // Reusable bar chart for both Revenue (raw USD, normalised by
  // `normalize` if entries arrive in mixed units) and EPS (already
  // raw $). Y-axis auto-scales to the max value with 20% headroom.
  if (!data || data.length === 0) return null;

  const normalized = data.map((d) => ({
    year: d.year,
    raw: normalize ? normalize(d) : d.value,
    original: d,
  }));

  const values = normalized.map((d) => d.raw || 0);
  const maxV = Math.max(...values);
  const minV = Math.min(...values, 0);
  const range = Math.max(Math.abs(maxV), Math.abs(minV)) * 1.2 || 1;

  const lastIdx = normalized.length - 1;
  const prev = lastIdx > 0 ? normalized[lastIdx - 1] : null;
  const last = normalized[lastIdx];
  const lastIsHigher = prev ? (last.raw || 0) >= (prev.raw || 0) : true;

  return (
    <div className="rounded-lg border bg-card p-4" data-testid={testId}>
      <h3 className="text-sm font-bold mb-4">{title}</h3>
      <div className="flex items-end gap-0">
        {/* Y-axis */}
        <div className="flex flex-col justify-between h-48 pr-2 shrink-0">
          {[1, 0.75, 0.5, 0.25, 0].map((frac, i) => (
            <span key={i} className="text-[10px] font-mono text-muted-foreground leading-none">
              {fmt(range * frac)}
            </span>
          ))}
        </div>
        {/* Bars */}
        <div className="flex-1 flex items-end gap-2 sm:gap-4 h-48 border-b border-l border-border/40 pl-1 pb-0">
          {normalized.map((d, i) => {
            const v = d.raw || 0;
            const heightPct = range > 0 ? (Math.abs(v) / range) * 100 : 0;
            const isLast = i === lastIdx;
            const negative = v < 0;

            let barColor;
            if (isLast) {
              barColor = lastIsHigher ? 'bg-green-500' : 'bg-red-500';
            } else if (negative) {
              barColor = 'bg-rose-700/60 dark:bg-rose-400/60';
            } else {
              barColor = 'bg-gray-700 dark:bg-white/90';
            }

            const prevBar = i > 0 ? normalized[i - 1] : null;
            const base = `${d.year}: ${fmt(v)}`;
            let label = base;
            if (prevBar && prevBar.raw && prevBar.raw !== 0) {
              const pct = ((v - prevBar.raw) / Math.abs(prevBar.raw)) * 100;
              const up = pct >= 0;
              const arrow = up ? '▲' : '▼';
              const sign = up ? '+' : '';
              const color = up ? 'text-emerald-400' : 'text-rose-400';
              label = (
                <>
                  {base}{' '}
                  <span className={color}>
                    ({arrow} {sign}{pct.toFixed(1)}% vs {prevBar.year})
                  </span>
                </>
              );
            }

            return (
              <div key={d.year} className="flex-1 flex flex-col items-center justify-end h-full">
                <HoverBar
                  label={label}
                  className={`w-full max-w-[60px] rounded-t-sm transition-all duration-500 ${barColor}`}
                  style={{ height: `${Math.max(heightPct, 2)}%` }}
                  testId={`non-div-bar-${d.year}`}
                  isDark={isDark}
                />
                <span className="text-[10px] font-mono text-muted-foreground mt-2">{d.year}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// Quality Score breakdown card. Reads the persisted
// `non_dividend_quality_signals` block and lays out the 7 inputs
// alongside their earned points so users can see why a stock got
// its tier instead of seeing the score as a black box.
function QualityScoreBreakdown({ signals, score, tier }) {
  if (!signals) return null;
  const points = signals.points || {};
  // Max points per signal per services/non_dividend_quality_score.py.
  const ROWS = [
    { key: 'rev_cagr', label: <>Revenue 3yr <AbbrTip term="CAGR">CAGR</AbbrTip></>, max: 25,
      value: fmtPct(signals.revenue_3yr_cagr) },
    { key: 'eps_cagr', label: <><AbbrTip term="EPS">EPS</AbbrTip> 3yr <AbbrTip term="CAGR">CAGR</AbbrTip></>, max: 20,
      value: fmtPct(signals.eps_3yr_cagr) },
    { key: 'roe', label: <><AbbrTip term="ROE">Return on Equity</AbbrTip></>, max: 15,
      value: fmtPct(signals.return_on_equity) },
    { key: 'fcf', label: <><AbbrTip term="FCF">Free Cash Flow</AbbrTip></>, max: 15,
      value: signals.free_cash_flow != null
        ? (signals.free_cash_flow >= 1e9
            ? `$${(signals.free_cash_flow / 1e9).toFixed(2)}B`
            : `$${(signals.free_cash_flow / 1e6).toFixed(1)}M`)
        : '—' },
    { key: 'de', label: <><AbbrTip term="DEBT_EQUITY">Debt / Equity</AbbrTip></>, max: 10,
      value: signals.debt_to_equity != null ? signals.debt_to_equity.toFixed(2) : '—' },
    { key: 'pe', label: <>Price / Earnings (<AbbrTip term="TRAILING_PE">P/E</AbbrTip>)</>, max: 10,
      value: signals.price_to_earnings != null ? signals.price_to_earnings.toFixed(1) : '—' },
    { key: 'gm', label: <>Gross Margin (<AbbrTip term="TTM">TTM</AbbrTip>)</>, max: 5,
      value: fmtPct(signals.gross_margin_ttm) },
  ];

  const tierColor =
    tier === 'GROWTH-A' ? 'text-emerald-600 dark:text-emerald-400'
    : tier === 'GROWTH-B' ? 'text-sky-600 dark:text-sky-400'
    : tier === 'GROWTH-C' ? 'text-amber-600 dark:text-amber-400'
    : 'text-rose-600 dark:text-rose-400';

  return (
    <div className="rounded-lg border bg-card p-4" data-testid="non-div-quality-breakdown">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold flex items-center gap-1.5">
          <Sparkles className="w-4 h-4 text-muted-foreground" />
          Quality Score Breakdown
        </h3>
        <div className="text-right">
          <p className={`text-xs font-bold uppercase tracking-wider ${tierColor}`} data-testid="non-div-tier">
            {tier || 'UNSCORED'}
          </p>
          <p className="text-lg font-bold font-mono" data-testid="non-div-score">
            {score != null ? `${score}/100` : '—'}
          </p>
        </div>
      </div>
      <div className="space-y-1.5">
        {ROWS.map((r) => {
          const earned = Number(points[r.key] || 0);
          const pct = (earned / r.max) * 100;
          return (
            <div key={r.key} className="flex items-center gap-3 text-xs" data-testid={`signal-${r.key}`}>
              <div className="w-36 shrink-0 text-muted-foreground">{r.label}</div>
              <div className="w-20 shrink-0 font-mono">{r.value}</div>
              <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full ${earned >= r.max * 0.7 ? 'bg-emerald-500' : earned >= r.max * 0.4 ? 'bg-amber-500' : 'bg-rose-500'}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
              <div className="w-12 shrink-0 text-right font-mono text-muted-foreground">
                {earned}/{r.max}
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-[10px] text-muted-foreground mt-3 italic">
        Rubric (100 pts): growth 45 (revenue 25 · EPS 20) · profitability 30 (ROE 15 · FCF 15) · balance sheet &amp; valuation 20 (D/E 10 · P/E 10) · gross margin 5.
        Missing or negative inputs score 0. Re-scored on the weeknight (Mon–Fri) admin chain from Massive Ratios + Income statements.
      </p>
    </div>
  );
}

// --- main tab --------------------------------------------------------

export default function NonDivGrowthTab({ ticker, isDark }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useAuth();
  const isAdmin = user && user.role === 'admin';

  useEffect(() => {
    if (!ticker) return;
    setLoading(true);
    setError(null);
    axios
      .get(`${API}/stocks/${ticker}/non-div-growth`, { withCredentials: true })
      .then((res) => setData(res.data))
      .catch(() => setError('Could not load growth data'))
      .finally(() => setLoading(false));
  }, [ticker]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="text-center">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2" />
          <p className="text-xs text-muted-foreground">Loading growth data...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div
        className="flex items-center justify-center min-h-[200px]"
        data-testid="non-div-growth-error"
      >
        <div className="text-center text-muted-foreground">
          <TrendingDown className="w-8 h-8 mx-auto mb-2" />
          <p className="text-sm">{error || 'No growth data available'}</p>
        </div>
      </div>
    );
  }

  const revenue = data.revenue;
  const eps = data.eps;
  const hasRevenue = revenue && Array.isArray(revenue.annual) && revenue.annual.length > 0;
  const hasEps = eps && Array.isArray(eps.annual) && eps.annual.length > 0;

  const revCagr = revenue?.cagr_pct;
  const epsCagr = eps?.cagr_pct;
  const revCagrColor =
    revCagr == null ? '' : revCagr >= 0
      ? 'text-emerald-600 dark:text-emerald-400'
      : 'text-rose-600 dark:text-rose-400';
  const epsCagrColor =
    epsCagr == null ? '' : epsCagr >= 0
      ? 'text-emerald-600 dark:text-emerald-400'
      : 'text-rose-600 dark:text-rose-400';

  const latestRev = hasRevenue ? revenue.annual[revenue.annual.length - 1] : null;
  const latestEps = hasEps ? eps.annual[eps.annual.length - 1] : null;
  const latestRevUsd = latestRev ? toRawUsd(latestRev) : null;

  return (
    <div className="space-y-3" data-testid="non-div-growth-tab">
      {/* Top-line summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MetricCard
          label="Revenue CAGR"
          value={revCagr != null ? `${revCagr >= 0 ? '+' : ''}${revCagr.toFixed(1)}%` : '—'}
          valueColor={revCagrColor}
          sub={revenue?.years ? `${revenue.years.toFixed(1)} yrs` : null}
        />
        <MetricCard
          label="Latest Revenue"
          value={latestRevUsd != null ? fmtUsdAuto(latestRevUsd) : '—'}
          sub={latestRev ? `${latestRev.year}` : null}
        />
        <MetricCard
          label="EPS CAGR"
          value={epsCagr != null ? `${epsCagr >= 0 ? '+' : ''}${epsCagr.toFixed(1)}%` : '—'}
          valueColor={epsCagrColor}
          sub={eps?.years ? `${eps.years.toFixed(1)} yrs` : null}
        />
        <MetricCard
          label="Latest EPS"
          value={latestEps ? fmtEpsBar(latestEps.value) : '—'}
          sub={latestEps ? `${latestEps.year}` : null}
        />
      </div>

      {/* Charts */}
      {hasRevenue && (
        <GrowthBarChart
          title={`Annual Revenue — Last ${revenue.annual.length} Years`}
          data={revenue.annual}
          fmt={fmtUsdAuto}
          normalize={toRawUsd}
          isDark={isDark}
          testId="non-div-revenue-chart"
        />
      )}
      {hasEps && (
        <GrowthBarChart
          title={`Diluted EPS — Last ${eps.annual.length} Years`}
          data={eps.annual}
          fmt={fmtEpsBar}
          isDark={isDark}
          testId="non-div-eps-chart"
        />
      )}

      {/* Empty state when Massive hasn't hydrated this ticker yet */}
      {!hasRevenue && !hasEps && (
        <div
          className="rounded-lg border bg-card p-6 text-center"
          data-testid="non-div-growth-empty"
        >
          <AlertCircle className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
          <p className="text-sm font-bold mb-1">Awaiting Massive financials</p>
          <p className="text-xs text-muted-foreground">
            Revenue and EPS history for this ticker hasn&apos;t been hydrated yet. The
            nightly Massive financials job will refresh it on the next run.
          </p>
        </div>
      )}

      {/* Quality Score breakdown */}
      <QualityScoreBreakdown
        signals={data.non_dividend_quality_signals}
        score={data.non_dividend_quality_score}
        tier={data.non_dividend_quality_tier}
      />

      {/* Admin-only data provenance — end users don't need source attribution. */}
      {isAdmin && (
        <p
          className="text-[10px] text-muted-foreground/70 text-right"
          data-testid="non-div-growth-data-source"
        >
          <span className="font-bold uppercase tracking-wider text-amber-700/80 dark:text-amber-300/80 mr-1">
            admin:
          </span>
          source{' '}
          <span className="font-semibold">
            {data.data_source === 'massive' ? 'Massive' : data.data_source || 'unknown'}
          </span>
          {data.non_dividend_quality_scored_at && (
            <> · scored {data.non_dividend_quality_scored_at.slice(0, 10)}</>
          )}
        </p>
      )}
    </div>
  );
}
